import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Props {
  productId: number;
  currentPrice: number;
  onClose: () => void;
}

export default function PriceAlert({ productId, currentPrice, onClose }: Props) {
  const [targetPrice, setTargetPrice] = useState(currentPrice * 0.9);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Fiyat alarmı kurmak için giriş yapmalısınız');
        setLoading(false);
        return;
      }

      if (targetPrice >= currentPrice) {
        setError('Hedef fiyat mevcut fiyattan düşük olmalıdır');
        setLoading(false);
        return;
      }

      const { error: insertError } = await supabase
        .from('price_alerts')
        .insert({
          user_id: user.id,
          product_id: productId,
          target_price: targetPrice,
          email: email || user.email,
          is_active: true
        });

      if (insertError) throw insertError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const discountPercentage = ((currentPrice - targetPrice) / currentPrice * 100).toFixed(0);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl text-gray-600"></i>
        </button>

        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-emerald-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Alarm Kuruldu!</h3>
            <p className="text-gray-600">
              Fiyat hedef seviyeye ulaştığında size e-posta ile bildirim göndereceğiz.
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-notification-line text-3xl text-rose-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Fiyat Alarmı Kur</h3>
              <p className="text-gray-600">
                Ürün fiyatı hedef seviyeye düştüğünde sizi bilgilendireceğiz
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Mevcut Fiyat
                </label>
                <div className="px-4 py-3 bg-gray-50 rounded-lg">
                  <span className="text-xl font-bold text-gray-900">{currentPrice.toFixed(2)} ₺</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Hedef Fiyat
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={currentPrice}
                    value={targetPrice}
                    onChange={(e) => setTargetPrice(parseFloat(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">₺</span>
                </div>
                {targetPrice < currentPrice && (
                  <p className="mt-2 text-sm text-emerald-600 flex items-center">
                    <i className="ri-arrow-down-line mr-1"></i>
                    %{discountPercentage} daha ucuz
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  E-posta Adresi (Opsiyonel)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Boş bırakırsanız hesabınızdaki e-posta kullanılacaktır
                </p>
              </div>

              {error && (
                <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                  <p className="text-sm text-rose-600 flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    {error}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-rose-500 text-white rounded-lg font-semibold hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Kuruluyor...' : 'Alarm Kur'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
